import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, X } from "lucide-react";
import "./TeamPawsWalkFeedbackPage.css";

import tablerBone from "../../assets/tabler_bone.png";
import mapDogPinIcon from "../../assets/dogHead.png";
import dogLevel0 from "../../assets/dogLevel5.png";
import dogLevel1 from "../../assets/dogLevel2.png";
import dogLevel2 from "../../assets/dogLevel4.png";
import TopNavBar from "../../components/TopNavBar";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";

const TakeTheLeashPage = () => {
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const isDraggingRef = useRef(false);

  // slider: 0 = drop, 1 = sniff, 2 = pass
  const [sliderValue, setSliderValue] = useState(1);

  // viewState:
  // 0 = initial
  // 1 = pass submitted
  const [viewState, setViewState] = useState(0);

  const [showTreatModal, setShowTreatModal] = useState(false);

  const feedbackOptions = [
    "I’d like to drop the leash\non this connection",
    "I’d like to keep sniffing\nout this connection",
    "Yes, I want to be one of\nDimsum’s trusted Pup Pals",
  ];

  const SLIDER_START = 12; // %
  const SLIDER_END = 88; // %
  const SLIDER_RANGE = SLIDER_END - SLIDER_START; // 90%
  const STEP_COUNT = 2; // 3 steps → 2 intervals
  const STEP_PERCENT = SLIDER_RANGE / STEP_COUNT; // 45%

  const thumbPosition = SLIDER_START + sliderValue * STEP_PERCENT;

  const dogLevelImages = {
    0: dogLevel0,
    1: dogLevel1,
    2: dogLevel2,
  };

  const getStepFromClientX = (clientX) => {
    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const percent = (x / rect.width) * 100;

    const rawStep = Math.round((percent - SLIDER_START) / STEP_PERCENT);

    return Math.min(Math.max(rawStep, 0), 2);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;

    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDragMove);
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const step = getStepFromClientX(clientX);
    setSliderValue(step);
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;

    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDragMove);
    document.removeEventListener("touchend", handleDragEnd);
  };

  const handleSubmit = () => {
    setViewState(1);
  };

  const handleBack = () => {
    if (viewState !== 0) {
      setViewState(0);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="feedback-container">
      {/* TOP NAV */}
      <div className="feedback-top-nav">
        <button className="back-nav-btn" onClick={handleBack}>
          <ArrowLeft size={24} color="#000" strokeWidth={3} />
          <span style={{ color: "rgba(0, 0, 0, 0.5)" }}>Back</span>
        </button>

        <div className="header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="feedback-content">
        {/* TITLE */}
        <h1 className="feedback-title">
          {viewState === 0 && "HOW DID TAKING THE LEASH WITH DIMSUM GO?"}

          {viewState === 1 && (
            <>
              CONGRATULATIONS!
              <br />
              YOU HAVE REACHED THE FINAL MILESTONE OF
              <br />
              YOUR DOG-SHARING JOURNEY:
              <br />
              FREELY DOG-SHARING
            </>
          )}
        </h1>

        {/* PROFILE CARD (hidden only in state 1) */}
        {viewState !== 1 && (
          <div className="feedback-profile-card">
            <img src={userPlaceholder} alt="Angela" className="profile-img" />
            <div className="profile-overlay">
              <div className="profile-info-row">
                <Heart size={26} strokeWidth={2} />
                <span className="profile-name"></span>
                {/* <span className="profile-dist">2.5mi away</span> */}
              </div>
            </div>
          </div>
        )}

        {/* QUESTION TEXT */}
        <p
          className={`feedback-question ${
            viewState === 1 ? "feedback-question--state-1" : ""
          }`}
        >
          {viewState === 0 && "Do you want to be Dimsum’s Trusted Pup Pal?"}

          {viewState === 1 && (
            <>
              We will share your choice with Dimsum & Jeanette to move you all
              forward on
              <br />
              your dog-sharing journey!
            </>
          )}
        </p>
        {viewState === 0 && (
          <p
            className={`feedback-question ${
              viewState === 0 ? "feedback-question--state-1" : ""
            }`}
          >
            {viewState === 0 &&
              "Being a Trusted Pup Pal grants access to Dimsum’s availability to book walks and playdates as you want!"}
          </p>
        )}
        {/* SLIDER (ONLY INITIAL STATE) */}
        {viewState === 0 && (
          <div className="feedback-slider-area" style={{ maxWidth: "800px" }}>
            <div
              className="slider-track-container-tpw"
              ref={sliderRef}
              style={{ width: "750px" }}
            >
              <div className="slider-track-bg-tpw"></div>
              <div
                className="slider-track-fill-tpw"
                style={{
                  // left: `${SLIDER_START}%`,
                  width: `${thumbPosition - SLIDER_START + SLIDER_START}%`,
                }}
              />

              {[0, 1, 2].map((step) => (
                <div
                  key={step}
                  className="slider-dot-wrapper"
                  style={{
                    left: `${SLIDER_START + step * STEP_PERCENT}%`,
                  }}
                  onClick={() => setSliderValue(step)}
                >
                  <div
                    className={`slider-dot ${
                      step <= sliderValue ? "active" : ""
                    }`}
                  />
                </div>
              ))}

              <div
                className="slider-thumb"
                style={{ left: `${thumbPosition}%` }}
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
              >
                <img
                  src={dogLevelImages[sliderValue]}
                  alt="Slider Dog"
                  style={{
                    width: sliderValue === 2 ? "70px" : "60px",
                    height: sliderValue === 2 ? "70px" : "60px",
                  }}
                />
              </div>
            </div>

            <div className="slider-labels-row">
              {feedbackOptions.map((text, index) => (
                <div
                  key={index}
                  className={`slider-label-item ${
                    index === sliderValue ? "selected" : ""
                  }`}
                  onClick={() => setSliderValue(index)}
                >
                  {text}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BUTTONS */}
        <div className="feedback-buttons-row">
          {viewState === 0 && (
            <>
              <button
                className="feedback-btn outline"
                onClick={() => setShowTreatModal(true)}
              >
                RATE/REVIEW ANGELA
              </button>

              <button className="feedback-btn filled" onClick={handleSubmit}>
                SUBMIT
              </button>
            </>
          )}
        </div>
      </div>

      {/* MODAL */}
      {showTreatModal && (
        <div className="treat-modal-overlay">
          <div className="treat-modal-card">
            <button
              className="treat-close-btn"
              onClick={() => setShowTreatModal(false)}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="treat-modal-content">
              <div className="treat-icons-row">
                <div className="cb-treat-dog-avatar">
                  <img src={mapDogPinIcon} alt="dogIcon" />
                </div>
                <img src={tablerBone} alt="bone" style={{ width: "60px" }} />
              </div>

              <h3 className="treat-high-paw" style={{ marginBottom: "1rem" }}>
                HIGH PAW!
              </h3>

              <h2 style={{ fontSize: "1.5rem" }}>
                We will share your response with Dimsum & Jeanette to move you
                all forward on your dog-sharing journey!
              </h2>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakeTheLeashPage;
